import React from 'react';
import { LegalPage, Section } from '../components/LegalLayout';
import { ShieldCheck, Lock, EyeOff, UserCheck } from 'lucide-react';
import SEO from '../components/SEO';

const PrivacyPolicy = () => (
  <div className="bg-white dark:bg-[#050505] transition-colors duration-300">
    <LegalPage 
      title="Privacy_Protocol" 
      subtitle="Digital Identity Protection & Asset Security Standards"
    >
      {/* SEO Engine Integration */}
      <SEO 
        title="Privacy Protocol | Data Protection" 
        description="Learn how THE_ARCHIVE protects your digital footprint. Our Zero-Knowledge protocol ensures your identity remains a rare asset."
        url="/privacy-policy"
      />

      {/* Trust Highlights - Pure Industrial Look */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
        <InfoCard icon={<Lock size={20}/>} title="Neural Encryption" desc="Military Standard" />
        <InfoCard icon={<EyeOff size={20}/>} title="Ghost Protocol" desc="Zero Tracking" />
        <InfoCard icon={<UserCheck size={20}/>} title="Full Ownership" desc="Sovereign Identity" />
      </div>

      {/* Intro Security Box - Pure Border Style */}
      <div className="bg-transparent p-10 md:p-12 rounded-[2.5rem] border-2 border-black/10 dark:border-white/10 mb-20 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-emerald-500/10 rounded-full blur-[100px]"></div>

        <div className="flex items-center gap-5 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center text-black shadow-[0_0_30px_rgba(16,185,129,0.3)]">
            <ShieldCheck size={26}/>
          </div>
          <div>
            <h3 className="text-sm font-black uppercase tracking-[0.4em] text-emerald-500">
              Privacy_First_Manifesto
            </h3>
            <p className="text-[10px] font-bold uppercase opacity-50 tracking-widest">Global Security Standards</p>
          </div>
        </div>

        <p className="text-sm font-bold uppercase tracking-tight leading-relaxed max-w-3xl opacity-80">
          THE_ARCHIVE operates as a vault-first ecosystem. Your privacy is treated as a <span className="text-emerald-500 italic">rare artifact</span>. 
          We strictly oppose data harvesting. Every bit of information processed is either encrypted in the vault or permanently purged upon request.
        </p>
      </div>

      {/* Main Policy Sections - Transparent & Bordered */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6">
          
          <SectionContainer>
            <Section 
              num="01" 
              title="Data Acquisition Logic" 
              text="We operate on a 'Minimum-Necessary' extraction framework. We only collect metadata critical for transaction verification and asset delivery. Once the acquisition is complete, all non-essential logs are encrypted and moved to an offline archive." 
            />
          </SectionContainer>

          <SectionContainer>
            <Section 
              num="02" 
              title="Financial Infrastructure" 
              text="Utilizing zero-knowledge payment processing. Our system never interacts with your raw financial data. Every transaction is shielded by PCI-DSS Level 1 protocols and multi-layer hardware firewalls. Financial privacy is our foundation." 
            />
          </SectionContainer>

          <SectionContainer>
            <Section 
              num="03" 
              title="Third-Party Nodes" 
              text="THE_ARCHIVE does not trade, rent, or monetize your digital identity. Data sharing is strictly limited to verified logistics partners and secure payment gateways. No marketing brokers, no ad-trackers, no identity leakages." 
            />
          </SectionContainer>

          <SectionContainer highlight={true}>
            <Section 
              num="04" 
              title="The Right to be Forgotten" 
              text="As a collector, you hold the master-key. You may trigger the 'Purge Protocol' at any time to request complete data erasure or export your historical records. Your digital footprint is entirely under your command." 
            />
          </SectionContainer>

        </div>
      </div>
    </LegalPage>
  </div>
);

// --- Styled Wrappers (Pure Theme) ---
const SectionContainer = ({ children, highlight = false }) => (
  <div className={`p-10 rounded-[2.5rem] border-2 transition-all duration-300 ${
    highlight 
    ? 'border-emerald-500/40 bg-emerald-500/5 shadow-[0_0_40px_rgba(16,185,129,0.05)]' 
    : 'border-black/5 dark:border-white/5 bg-transparent hover:border-emerald-500/30'
  }`}>
    {children}
  </div>
);

// --- Industrial Info Card (Pure Theme) ---
const InfoCard = ({ icon, title, desc }) => (
  <div className="p-10 rounded-[2.5rem] border-2 border-black/5 dark:border-white/5 bg-transparent flex flex-col items-center text-center space-y-5 transition-all duration-500 hover:border-emerald-500">
    <div className="w-14 h-14 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center text-black dark:text-white border border-black/10 dark:border-white/10 group-hover:text-emerald-500 transition-colors">
      {icon}
    </div>
    <div className="space-y-2">
      <p className="text-[11px] font-black uppercase tracking-[0.3em] text-black dark:text-white">{title}</p>
      <p className="text-[9px] text-emerald-500 uppercase font-black italic tracking-[0.2em]">{desc}</p>
    </div>
  </div>
);

export default PrivacyPolicy;